'use client'

import * as React from 'react'
import { Link } from 'react-router-dom'
import { Filter, Plus, Search } from 'lucide-react'
import { collection, getDocs } from 'firebase/firestore'
import { Badge } from 'components/ui/badge'
import { Button } from 'components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card'
import { Input } from 'components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select'
import { db } from '../../../firebase'
import { useUser } from 'components/context/user-context'
import { getUserId, isUserAssociatedWithProposal } from 'lib/access'

const PROPOSAL_STATUS_LABELS = {
  draft: 'Draft',
  submitted: 'Submitted',
  review: 'Under Review',
  revisions: 'Revisions',
  approved: 'Approved',
  won: 'Won',
  lost: 'Lost',
}

const STATUS_BADGE_STYLES = {
  draft: 'bg-muted text-muted-foreground border-border',
  submitted: 'bg-primary/15 text-primary border-primary/30',
  review: 'bg-warning/15 text-warning border-warning/30',
  revisions: 'bg-secondary/50 text-secondary-foreground border-secondary',
  approved: 'bg-success/15 text-success border-success/30',
  won: 'bg-emerald-500/15 text-emerald-700 border-emerald-500/30',
  lost: 'bg-destructive/15 text-destructive border-destructive/30',
}

function formatCurrency(value, currency = 'KES') {
  const amount = Number.isFinite(value) ? value : 0
  return new Intl.NumberFormat('en-KE', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(amount)
}

function formatDate(value) {
  if (!value) return '—'
  if (typeof value?.toDate === 'function') {
    return value.toDate().toLocaleDateString('en-KE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return '—'
  return parsed.toLocaleDateString('en-KE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

function StatusBadge({ status }) {
  const label = PROPOSAL_STATUS_LABELS[status] || status || 'Unknown'
  const style = STATUS_BADGE_STYLES[status] || 'bg-muted text-muted-foreground border-border'
  return (
    <Badge className={`text-[10px] border ${style}`}>
      {label}
    </Badge>
  )
}

function EmptyState({ title, description }) {
  return (
    <Card className="border border-dashed border-border">
      <CardContent className="flex flex-col items-center justify-center gap-2 py-12 text-center">
        <div className="flex size-12 items-center justify-center rounded-full bg-primary/10">
          <Plus className="size-5 text-primary" />
        </div>
        <p className="text-sm font-medium text-foreground">{title}</p>
        <p className="max-w-sm text-xs text-muted-foreground">{description}</p>
      </CardContent>
    </Card>
  )
}

export default function ProposalsPage() {
  const { user, accessLevel, canEdit } = useUser()
  const [search, setSearch] = React.useState('')
  const [statusFilter, setStatusFilter] = React.useState('all')
  const [proposals, setProposals] = React.useState([])
  const [isLoading, setIsLoading] = React.useState(true)

  React.useEffect(() => {
    let isMounted = true

    async function loadProposals() {
      try {
        const snapshot = await getDocs(collection(db, 'proposals'))
        if (!isMounted) return
        const records = snapshot.docs.map((docItem) => ({
          id: docItem.id,
          ...docItem.data(),
        }))
        setProposals(records)
      } catch {
        setProposals([])
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    loadProposals()

    return () => {
      isMounted = false
    }
  }, [])

  const limited = accessLevel === 'limited'
  const currentUserId = getUserId(user)
  const visibleProposals = limited
    ? proposals.filter((proposal) => isUserAssociatedWithProposal(proposal, currentUserId))
    : proposals

  const filtered = visibleProposals.filter((proposal) => {
    const titleValue = (proposal.title || '').toLowerCase()
    const authorValue = (proposal.submittedByName || '').toLowerCase()
    const matchesSearch =
      titleValue.includes(search.toLowerCase()) || authorValue.includes(search.toLowerCase())
    const matchesStatus = statusFilter === 'all' || proposal.status === statusFilter
    return matchesSearch && matchesStatus
  })

  const totalValue = visibleProposals.reduce((sum, proposal) => sum + (proposal.totalBudget || 0), 0)
  const statusKeys = Object.keys(PROPOSAL_STATUS_LABELS)

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-serif tracking-tight">
            Proposals
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage research proposals and track review workflows
          </p>
        </div>
        {canEdit ? (
          <Button asChild className="bg-primary text-primary-foreground hover:bg-accent gap-2">
            <Link to="/dashboard/proposals/new">
              <Plus className="size-4" />
              New Proposal
            </Link>
          </Button>
        ) : null}
      </div>

      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search proposals..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48">
            <Filter className="mr-1 size-4" />
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {Object.entries(PROPOSAL_STATUS_LABELS).map(([key, label]) => (
              <SelectItem key={key} value={key}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid gap-3 grid-cols-2 sm:grid-cols-4">
        <div className="rounded-lg bg-muted/50 p-3 text-center">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Total</p>
          <p className="text-xl font-bold text-foreground">{visibleProposals.length}</p>
        </div>
        <div className="rounded-lg bg-muted/50 p-3 text-center">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Under Review</p>
          <p className="text-xl font-bold text-primary">
            {visibleProposals.filter((proposal) => !['draft', 'won', 'lost'].includes(proposal.status)).length}
          </p>
        </div>
        <div className="rounded-lg bg-muted/50 p-3 text-center">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Won</p>
          <p className="text-xl font-bold text-emerald-600">
            {visibleProposals.filter((proposal) => proposal.status === 'won').length}
          </p>
        </div>
        <div className="rounded-lg bg-muted/50 p-3 text-center">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Total Value</p>
          <p className="text-xl font-bold text-foreground">{formatCurrency(totalValue)}</p>
        </div>
      </div>

      {isLoading ? (
        <EmptyState title="Loading proposals..." description="Fetching data from Firestore." />
      ) : filtered.length === 0 ? (
        <EmptyState
          title="No proposals found"
          description={
            search || statusFilter !== 'all'
              ? 'Try adjusting your search or filters.'
              : 'Create your first research proposal to get started.'
          }
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {filtered.map((proposal) => {
            const createdAt = proposal.createdAt || proposal.updatedAt
            const totalBudget = proposal.totalBudget || 0
            return (
              <Link key={proposal.id} to={`/dashboard/proposals/${proposal.id}`}>
                <Card className="h-full cursor-pointer border border-border transition-shadow hover:shadow-md">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <StatusBadge status={proposal.status || 'draft'} />
                        <CardTitle className="mt-2 text-sm font-semibold text-foreground leading-tight">
                          {proposal.title}
                        </CardTitle>
                        <p className="mt-0.5 text-xs text-muted-foreground line-clamp-1">
                          {proposal.departmentId || 'No department'} · By {proposal.submittedByName || 'Unknown'}
                        </p>
                      </div>
                      <div className="shrink-0 text-right">
                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Budget</p>
                        <p className="text-sm font-semibold text-foreground">
                          {formatCurrency(totalBudget, proposal.currency)}
                        </p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="flex flex-col gap-3">
                    <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                      <span>Created {formatDate(createdAt)}</span>
                      <span className="capitalize">
                        {(proposal.type || 'proposal').replace('_', ' ')}
                      </span>
                    </div>
                    {Array.isArray(proposal.reviewScores) && proposal.reviewScores.length > 0 ? (
                      <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                        <span className="uppercase tracking-wider">Avg Score:</span>
                        <span className="text-xs font-semibold text-foreground">
                          {Math.round(
                            proposal.reviewScores.reduce((sum, review) => sum + review.total, 0) /
                              proposal.reviewScores.length,
                          )}
                          /100
                        </span>
                        <span>
                          ({proposal.reviewScores.length} review
                          {proposal.reviewScores.length !== 1 ? 's' : ''})
                        </span>
                      </div>
                    ) : null}
                    <div className="flex items-center gap-1">
                      {statusKeys.map((stage, index) => {
                        const currentIndex = statusKeys.indexOf(proposal.status)
                        const filled = index <= currentIndex
                        return (
                          <div
                            key={stage}
                            className="h-1.5 flex-1 rounded-full transition-colors"
                            style={{
                              backgroundColor: filled
                                ? 'hsl(var(--primary))'
                                : 'hsl(var(--muted))',
                            }}
                          />
                        )
                      })}
                    </div>
                  </CardContent>
                </Card>
              </Link>
            )
          })}
        </div>
      )}
    </div>
  )
}
